/**
 * Partnr's ghost cards: one system for "Partnr is putting things here".
 *
 * While a write tool's input streams in (contract section 3), the ghost
 * layer (components/canvas/PartnrGhostLayer.tsx) reserves one open block per
 * toolCallId and draws a ghost per streamed item. When the action applies,
 * the executor takes the reservation, creates the real items at the ghosts'
 * positions in one insert, and lands the block: ghosts fade out in order over
 * the real cards. On output-error or output-denied the block fades.
 *
 * The executor uses the same registry when an action arrives with no
 * streamed ghosts (the old partnr-chat stream, a reload), so there is one
 * ghost system, not two.
 *
 * Pure layout plus a small zustand store; nothing here reads the canvas.
 */
import { create } from 'zustand';
import { getDefaultHeight, getDefaultWidth } from '@/lib/canvasActions';

/** Tools whose input becomes ghosts while it streams. */
export const GHOST_TOOLS = new Set(['create_canvas_items', 'create_diagram', 'add_links', 'add_images', 'clip_to_board']);

export const LINK_CARD_WIDTH = 380;
export const IMAGE_CARD_MAX_WIDTH = 360;
export const CLIP_CARD_WIDTH = 320;
const GAP = 40;

export type GhostKind = 'card' | 'node' | 'link' | 'image' | 'quote';

/** One streamed item, before it has a position. */
export interface GhostSpec {
  kind: GhostKind;
  itemType: string;
  title: string;
  body: string;
  color?: string;
  width: number;
  height: number;
  /** The item's input is complete (the next one has started, or the input is) */
  complete: boolean;
  /** Diagram node id, so edges can find their ends */
  nodeId?: string;
}

export interface GhostSlot extends GhostSpec {
  x: number;
  y: number;
}

export type GhostPhase = 'streaming' | 'ready' | 'landing' | 'fading';

export interface GhostBlock {
  key: string;
  toolName: string;
  boardId: string | null;
  origin: { x: number; y: number };
  /** Space reserved when the block was made; it may grow past it */
  size?: { width: number; height: number };
  columns: number;
  cell: { width: number; height: number };
  slots: GhostSlot[];
  /** Diagram edges by slot index; drawn once both ends exist */
  edges: Array<[number, number]>;
  phase: GhostPhase;
  /** Set by the executor when it took the positions */
  taken: boolean;
  updatedAt: number;
}

interface Layout {
  columns: number;
  cell: { width: number; height: number };
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * Block shape per tool: how many per row, and a typical card size for
 * reserving space before the items arrive. Positions themselves come from
 * flowPositions, from the real sizes.
 */
export function layoutFor(toolName: string, input: Record<string, unknown>): Layout {
  switch (toolName) {
    case 'create_canvas_items': {
      const layout = str(input.layout);
      const columns = layout === 'horizontal_row' ? 12 : layout === 'vertical_column' ? 1 : 4;
      return { columns, cell: { width: 220, height: 200 } };
    }
    case 'create_diagram':
      return { columns: 4, cell: { width: 280, height: 220 } };
    case 'add_links':
      return { columns: 3, cell: { width: LINK_CARD_WIDTH, height: 280 } };
    case 'add_images':
      return { columns: 3, cell: { width: IMAGE_CARD_MAX_WIDTH, height: 260 } };
    default:
      return { columns: 1, cell: { width: CLIP_CARD_WIDTH, height: 200 } };
  }
}

/** Expected item count when reserving space, before the input says. */
export function expectedCount(toolName: string): number {
  return toolName === 'clip_to_board' ? 1 : toolName === 'create_diagram' ? 8 : 6;
}

export const IMAGE_CARD_MAX_HEIGHT = 480;

/** Image card size for a known pixel size: at most 360 wide and 480 tall, true to its shape. */
export function imageCardSize(width: number | null | undefined, height: number | null | undefined): { width: number; height: number } {
  if (!width || !height) return { width: IMAGE_CARD_MAX_WIDTH, height: 240 };
  const scale = Math.min(IMAGE_CARD_MAX_WIDTH / width, IMAGE_CARD_MAX_HEIGHT / height, 1);
  return { width: Math.max(60, Math.round(width * scale)), height: Math.max(60, Math.round(height * scale)) };
}

/** Clip card height from the quote length, so the ghost and the card match. */
export function clipCardHeight(quote: string, note: string): number {
  const lines = Math.ceil(quote.length / 38) + (note ? Math.ceil(note.length / 44) + 1 : 0) + 2;
  return Math.min(520, Math.max(140, 48 + lines * 20));
}

/**
 * The ghosts a tool's (partial) input describes. Items in a folder other than
 * the current board make no ghosts: the user is not looking at that board.
 */
export function ghostSpecs(toolName: string, input: unknown, inputComplete: boolean): { specs: GhostSpec[]; edges: Array<[string, string]> } {
  const args = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  if (str(args.board_id) || str(args.folder_id) || str(args.folder_name)) return { specs: [], edges: [] };
  const list = (key: string) => (Array.isArray(args[key]) ? (args[key] as unknown[]) : []).filter((v) => v && typeof v === 'object') as Array<Record<string, unknown>>;
  const isComplete = (i: number, n: number) => inputComplete || i < n - 1;

  switch (toolName) {
    case 'create_canvas_items': {
      const items = list('items');
      return {
        specs: items.map((item, i) => {
          const type = str(item.item_type) || 'sticky';
          return {
            kind: 'card',
            itemType: type,
            title: str(item.title),
            body: str(item.body),
            color: str(item.color) || undefined,
            width: getDefaultWidth(type),
            height: getDefaultHeight(type),
            complete: isComplete(i, items.length),
          };
        }),
        edges: [],
      };
    }
    case 'create_diagram': {
      const nodes = list('nodes');
      const edges = list('edges')
        .map((e) => [str(e.source_id), str(e.target_id)] as [string, string])
        .filter(([a, b]) => a && b);
      return {
        specs: nodes.map((node, i) => {
          const type = str(node.item_type) || 'sticky';
          return {
            kind: 'node',
            itemType: type,
            title: str(node.title),
            body: str(node.body),
            color: str(node.color) || undefined,
            width: getDefaultWidth(type),
            height: getDefaultHeight(type),
            complete: isComplete(i, nodes.length),
            nodeId: str(node.id) || undefined,
          };
        }),
        edges,
      };
    }
    case 'add_links': {
      const urls = (Array.isArray(args.urls) ? args.urls : []).filter((u): u is string => typeof u === 'string');
      const links = list('links');
      const all = [
        ...links.map((l) => ({ url: str(l.url), title: str(l.title), body: str(l.description), image: !!str(l.image) })),
        ...urls.map((url) => ({ url, title: '', body: '', image: false })),
      ].filter((l) => l.url);
      return {
        specs: all.map((l, i) => ({
          kind: 'link',
          itemType: 'link',
          title: l.title || hostOf(l.url),
          body: l.body,
          width: LINK_CARD_WIDTH,
          height: l.image ? 280 : 120,
          complete: isComplete(i, all.length),
        })),
        edges: [],
      };
    }
    case 'add_images': {
      const images = list('images').filter((img) => str(img.url));
      return {
        specs: images.map((img, i) => ({
          kind: 'image',
          itemType: 'image',
          title: str(img.caption),
          body: '',
          width: IMAGE_CARD_MAX_WIDTH,
          height: 240,
          complete: isComplete(i, images.length),
        })),
        edges: [],
      };
    }
    case 'clip_to_board': {
      const quote = str(args.quote);
      if (!quote) return { specs: [], edges: [] };
      const note = str(args.note);
      return {
        specs: [{
          kind: 'quote',
          itemType: 'text_card',
          title: str(args.source_title),
          body: quote,
          width: CLIP_CARD_WIDTH,
          height: clipCardHeight(quote, note),
          complete: inputComplete,
        }],
        edges: [],
      };
    }
    default:
      return { specs: [], edges: [] };
  }
}

/**
 * Rows of `columns` items, left to right, each row under the tallest item of
 * the row above. An item's position depends only on the items before it, so
 * ghosts that stream in later never move earlier ones, and the executor
 * reaches the same positions from the real sizes.
 */
export function flowPositions(sizes: Array<{ width: number; height: number }>, columns: number, origin: { x: number; y: number }): Array<{ x: number; y: number }> {
  const out: Array<{ x: number; y: number }> = [];
  let x = origin.x;
  let y = origin.y;
  let rowHeight = 0;
  sizes.forEach((size, i) => {
    if (i > 0 && i % Math.max(1, columns) === 0) {
      x = origin.x;
      y += rowHeight + GAP;
      rowHeight = 0;
    }
    out.push({ x, y });
    x += size.width + GAP;
    rowHeight = Math.max(rowHeight, size.height);
  });
  return out;
}

/** Size of a block that holds `count` typical items, for reserving open space. */
export function blockSize(layout: Layout, count: number): { width: number; height: number } {
  const columns = Math.min(layout.columns, Math.max(1, count));
  const rows = Math.ceil(Math.max(1, count) / layout.columns);
  return { width: columns * layout.cell.width + (columns - 1) * GAP, height: rows * layout.cell.height + (rows - 1) * GAP };
}

/** How many typical items fit side by side in `width` canvas pixels (at least one). */
export function columnsThatFit(layout: Layout, width: number): number {
  return Math.max(1, Math.min(layout.columns, Math.floor((width + GAP) / (layout.cell.width + GAP))));
}

/** Slots for `specs`: earlier slots keep their position, only their text and state update. */
export function placeSlots(previous: GhostSlot[], specs: GhostSpec[], layout: Layout, origin: { x: number; y: number }): GhostSlot[] {
  const flow = flowPositions(specs, layout.columns, origin);
  return specs.map((spec, i) => {
    const before = previous[i];
    if (before) return { ...spec, x: before.x, y: before.y };
    return { ...spec, ...flow[i] };
  });
}

/** Edges as slot index pairs, only where both ends exist. */
export function edgeSlots(specs: GhostSpec[], edges: Array<[string, string]>): Array<[number, number]> {
  const index = new Map(specs.map((s, i) => [s.nodeId, i]));
  return edges
    .map(([a, b]) => [index.get(a), index.get(b)] as [number | undefined, number | undefined])
    .filter((e): e is [number, number] => e[0] !== undefined && e[1] !== undefined);
}

/** Space live ghost blocks take, so a second block (a parallel tool call) does not land on the first. */
export function ghostObstacles(blocks: Record<string, GhostBlock>): Array<{ id: string; x: number; y: number; width: number; height: number }> {
  return Object.values(blocks)
    .filter((b) => b.phase !== 'fading')
    .map((b) => {
      let maxX = b.origin.x + (b.size?.width ?? 0);
      let maxY = b.origin.y + (b.size?.height ?? 0);
      let minX = b.origin.x;
      let minY = b.origin.y;
      for (const s of b.slots) {
        minX = Math.min(minX, s.x);
        minY = Math.min(minY, s.y);
        maxX = Math.max(maxX, s.x + s.width);
        maxY = Math.max(maxY, s.y + s.height);
      }
      return { id: `ghost:${b.key}`, x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    });
}

// How long ghosts take to leave: landing staggers per slot, fading is one step
export const LAND_STAGGER_MS = 60;
export const GHOST_FADE_MS = 280;

interface GhostState {
  blocks: Record<string, GhostBlock>;
  /** Creates the block once per key; later calls do nothing (the first reservation wins). */
  reserve: (block: Omit<GhostBlock, 'phase' | 'taken' | 'updatedAt' | 'slots' | 'edges'> & { slots?: GhostSlot[] }) => void;
  /** New streamed input: grows the block without moving earlier ghosts. */
  update: (key: string, specs: GhostSpec[], edges: Array<[string, string]>, inputComplete: boolean) => void;
  /** Moves slots (a diagram's final layout, or a placement the server resolved). */
  moveSlots: (key: string, positions: Array<{ x: number; y: number } | undefined>) => void;
  /** Keeps only these slots, in this order (links the executor skipped as already on the board). */
  keepSlots: (key: string, indexes: number[]) => void;
  /** The executor takes the positions; null when there is no live block for this key. */
  take: (key: string) => GhostBlock | null;
  /** Real items are in: ghosts leave in order over them, then the block goes. */
  land: (key: string) => void;
  /** The tool failed or was cancelled: ghosts fade, then the block goes. */
  fade: (key: string) => void;
  remove: (key: string) => void;
  clear: () => void;
}

const timers = new Map<string, ReturnType<typeof setTimeout>>();

function clearTimer(key: string) {
  const existing = timers.get(key);
  if (existing) clearTimeout(existing);
  timers.delete(key);
}

function later(key: string, ms: number, fn: () => void) {
  clearTimer(key);
  timers.set(key, setTimeout(() => {
    timers.delete(key);
    fn();
  }, ms));
}

export const usePartnrGhostStore = create<GhostState>((set, get) => ({
  blocks: {},

  reserve: (block) => {
    if (get().blocks[block.key]) return;
    // A fade or landing still pending under this key must not remove the new block
    clearTimer(block.key);
    set((state) => ({
      blocks: {
        ...state.blocks,
        [block.key]: { slots: [], ...block, edges: [], phase: 'streaming', taken: false, updatedAt: Date.now() },
      },
    }));
  },

  update: (key, specs, edges, inputComplete) => {
    const block = get().blocks[key];
    if (!block || block.phase === 'landing' || block.phase === 'fading') return;
    const slots = placeSlots(block.slots, specs, { columns: block.columns, cell: block.cell }, block.origin);
    set((state) => ({
      blocks: {
        ...state.blocks,
        [key]: { ...block, slots, edges: edgeSlots(specs, edges), phase: inputComplete ? 'ready' : 'streaming', updatedAt: Date.now() },
      },
    }));
  },

  moveSlots: (key, positions) => {
    const block = get().blocks[key];
    if (!block) return;
    set((state) => ({
      blocks: {
        ...state.blocks,
        [key]: { ...block, slots: block.slots.map((s, i) => (positions[i] ? { ...s, ...positions[i]! } : s)), updatedAt: Date.now() },
      },
    }));
  },

  keepSlots: (key, indexes) => {
    const block = get().blocks[key];
    if (!block) return;
    const slots = indexes.map((i) => block.slots[i]).filter((slot): slot is GhostSlot => !!slot);
    set((state) => ({ blocks: { ...state.blocks, [key]: { ...block, slots, edges: [], updatedAt: Date.now() } } }));
  },

  take: (key) => {
    const block = get().blocks[key];
    if (!block || block.phase === 'fading' || block.taken) return null;
    set((state) => ({ blocks: { ...state.blocks, [key]: { ...block, taken: true } } }));
    return block;
  },

  land: (key) => {
    const block = get().blocks[key];
    if (!block) return;
    set((state) => ({ blocks: { ...state.blocks, [key]: { ...block, phase: 'landing', updatedAt: Date.now() } } }));
    later(key, GHOST_FADE_MS + block.slots.length * LAND_STAGGER_MS, () => get().remove(key));
  },

  fade: (key) => {
    const block = get().blocks[key];
    if (!block || block.phase === 'landing' || block.phase === 'fading') return;
    set((state) => ({ blocks: { ...state.blocks, [key]: { ...block, phase: 'fading', updatedAt: Date.now() } } }));
    later(key, GHOST_FADE_MS, () => get().remove(key));
  },

  remove: (key) => {
    clearTimer(key);
    set((state) => {
      if (!state.blocks[key]) return state;
      const { [key]: _gone, ...rest } = state.blocks;
      return { blocks: rest };
    });
  },

  clear: () => {
    for (const t of timers.values()) clearTimeout(t);
    timers.clear();
    set({ blocks: {} });
  },
}));
